// ── Account API client ──────────────────────────────────────────────────────
//
// Every account call goes through `request()`, which handles the base URL,
// auth headers, JSON serialisation and uniform error handling.
//
// Auth here is header-based: the signed-in user's `uid`/`pin` are replayed on
// every authenticated call. There is no token exchange and no refresh.

const BASE_URL = "https://api-eeighttech.azure-api.net/core/api/v1";
const API_KEY = `${process.env.REACT_APP_ACCOUNT_XAPI_KEY}`;

// The base for every anonymous call — the reset-pin pair and the unsubscribe
// page. Those calls must land in the environment that ISSUED the link they came
// from (prod links → core — the APIM refresh is live; staging links →
// node-asia). pl.eeighttech.com serves prod links, so the default is core; flip
// REACT_APP_RESETPIN_BASE_URL (in .env.production) to "…/node-asia" while
// testing staging-issued links. The env var keeps its historic name because
// that name is what is set in the deployed app settings; it moves both flows
// together, which is right — a staging-issued mail could carry either link.
const PUBLIC_BASE_URL =
	process.env.REACT_APP_RESETPIN_BASE_URL ??
	"https://api-eeighttech.azure-api.net/core/api/v1";

export type Credentials = {
	_uid: string;
	_pin: string;
};

export type MetadataUser = {
	Id?: string;
	UserId?: string;
	DisplayName?: string;
	CreatedDate?: string;
	RecentLoginTimestamp?: number;
	Metadata?: Record<string, string>;
};

export type MetadataResponse = {
	User?: MetadataUser;
	Metadata?: Record<string, string>;
};

export type SupportTicketPayload = {
	message: string;
	phone: string;
};

/**
 * Email preferences — `suppressed` is a denylist of area ids the user has
 * opted OUT of (e.g. ["promotions"]); empty means subscribed to everything.
 * `areas` is the server's canonical taxonomy, used only to notice drift.
 */
export type EmailPreferences = {
	suppressed: string[];
	areas?: string[];
};

/**
 * Response for validating a PIN-reset link
 * (GET /api/v1/pub/resetpin/{guid}?uid=…). PascalCase comes straight off the
 * service entity.
 */
export type ResetPinStatus = {
	Valid: boolean;
	Reason: string;
	ExpiresAt: number;
};

// ── Errors ──────────────────────────────────────────────────────────────────

export class ApiError extends Error {
	status?: number;

	constructor(status?: number, message?: string) {
		super(message || `API error ${status}`);
		this.name = "ApiError";
		this.status = status;
		// `target: es5` severs the prototype chain when extending built-ins.
		Object.setPrototypeOf(this, ApiError.prototype);
	}
}

/**
 * Read a status code off a caught value. Under `strict`, catch bindings are
 * `unknown`, and the es5 target makes `instanceof ApiError` unreliable.
 */
export function errorStatus(err: unknown): number | undefined {
	if (typeof err === "object" && err !== null && "status" in err) {
		const status = (err as { status?: unknown }).status;
		if (typeof status === "number") return status;
	}
	return undefined;
}

// ── Core request ────────────────────────────────────────────────────────────

type ResponseType = "json" | "blob" | "text" | "none";

type RequestOptions = {
	method?: string;
	/** Signed-in user — adds the uid/pin auth headers. */
	user?: Credentials;
	/** JSON body; sets content-type automatically. */
	body?: unknown;
	responseType?: ResponseType;
};

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
	const { method = "GET", user, body, responseType = "json" } = opts;

	const headers: Record<string, string> = {
		aid: "default",
		"x-api-key": API_KEY,
		XApiKey: API_KEY, // some endpoints use PascalCase
		...(user ? { uid: user._uid, pin: user._pin } : {}),
		...(body ? { "content-type": "application/json" } : {}),
	};

	const res = await fetch(`${BASE_URL}${path}`, {
		method,
		headers,
		...(body ? { body: JSON.stringify(body) } : {}),
	});

	if (!res.ok) throw new ApiError(res.status);

	if (responseType === "blob") return (await res.blob()) as unknown as T;
	if (responseType === "none") return null as unknown as T;
	if (responseType === "text") return (await res.text()) as unknown as T;

	// Try JSON; fall back to text (handles plain-number/string responses).
	const text = await res.text();
	try {
		return JSON.parse(text) as T;
	} catch {
		return text as unknown as T;
	}
}

// ── Public request ───────────────────────────────────────────────────────────
//
// These are the calls a signed-out reader makes from a link in a mail: the
// reset-pin link (where the guid is the credential) and the unsubscribe page
// (where the typed address is the whole request). They send NO auth/API-key
// headers at all — the server side is the /api/v1/pub family, which the API-key
// middleware exempts for exactly this reason.

type PublicRequestOptions = {
	method?: string;
	body?: unknown;
};

export async function publicRequest<T>(
	path: string,
	opts: PublicRequestOptions = {}
): Promise<T> {
	const { method = "GET", body } = opts;

	const res = await fetch(`${PUBLIC_BASE_URL}${path}`, {
		method,
		...(body ? { body: JSON.stringify(body) } : {}),
		// Content-type is only set when there is a body; everything else about
		// the request is bare.
		...(body ? { headers: { "content-type": "application/json" } } : {}),
	});

	if (!res.ok) throw new ApiError(res.status);

	const text = await res.text();
	if (!text) return null as unknown as T;
	try {
		return JSON.parse(text) as T;
	} catch {
		return text as unknown as T;
	}
}

// ── Account ─────────────────────────────────────────────────────────────────

const account = {
	/** Fetch user metadata. Doubles as the sign-in call. */
	getMetadata(uid: string, pin: string) {
		return request<MetadataResponse>("/metadata", {
			user: { _uid: uid, _pin: pin },
		});
	},

	/** Fetch the account avatar image — resolves with a Blob. */
	getAvatarImage(email: string, user: Credentials) {
		return request<Blob>(`/content/accountimage/${encodeURIComponent(email)}`, {
			user,
			responseType: "blob",
		});
	},

	/** Submit a support ticket — resolves with the ticket number. */
	postSupportTicket(payload: SupportTicketPayload, user: Credentials) {
		return request<unknown>("/support/new", {
			method: "POST",
			user,
			body: payload,
		}).then((data) => {
			if (data !== null && typeof data === "object") {
				const d = data as Record<string, unknown>;
				return String(d.ticketNumber ?? d.ticket ?? d.id ?? data);
			}
			return String(data); // plain string or number
		});
	},

	/**
	 * Fetch the signed-in user's email preferences (the opt-out denylist for
	 * campaign mail areas). Transactional mail has no area and is always sent.
	 */
	getEmailPreferences(user: Credentials) {
		return request<EmailPreferences>("/account/emailpreferences", { user });
	},

	/**
	 * Replace the denylist wholesale — an area left out of `suppressed` is
	 * subscribed. Returns the server's canonicalised echo.
	 */
	setEmailPreferences(suppressed: string[], user: Credentials) {
		return request<EmailPreferences>("/account/emailpreferences", {
			method: "PUT",
			user,
			body: { suppressed },
		});
	},

	/**
	 * Permanently delete the account. This endpoint takes the credentials in
	 * the JSON body and sends no API key, unlike every other call above.
	 */
	async deleteAccount(email: string, pin: string): Promise<void> {
		const res = await fetch(`${BASE_URL}/account/delete`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ userId: email, pin }),
		});

		if (!res.ok) throw new ApiError(res.status, `API Error: ${res.status}`);
	},

	/**
	 * Validate a PIN-reset link. The token is base64url (A–Z a–z 0–9 - _ only),
	 * so it goes into the query string verbatim — no URL-encoding.
	 *
	 * 200 → { Valid: true } means the reset form can be shown; 200 with
	 * Valid: false means the link expired; 404 means it was never issued,
	 * already consumed, or the token is bad.
	 */
	validateResetPin(guid: string, uid: string) {
		return publicRequest<ResetPinStatus>(`/pub/resetpin/${guid}?uid=${uid}`);
	},

	/**
	 * Set a new PIN, consuming the one-time reset link. Anonymous POST — same
	 * trust model as validate: the guid is the credential, the uid token
	 * identifies the owner. Succeeds with 2xx; the link is single-use, so a
	 * second POST with the same guid is expected to fail (404/400).
	 */
	setResetPin(guid: string, uid: string, newPin: string) {
		return publicRequest<unknown>(`/pub/resetpin/${guid}/pin`, {
			method: "POST",
			body: { uid, pin: newPin },
		});
	},

	/**
	 * Opt an address out of campaign mail, from the anonymous unsubscribe page.
	 *
	 * The address is the whole request: the unsubscribe link in a mail footer is
	 * one URL for every recipient, so the page has to ask who is asking. The
	 * service records the opt-out in the SES contact list every campaign send
	 * already names.
	 *
	 * 200 → { unsubscribed: true }. The service answers 200 for any address it
	 * will take, whether or not it was already suppressed, so a 200 here never
	 * means "you were on the list" — only that you now are off it. 400 is the
	 * address being unusable; anything else is the service failing, which the
	 * page must not report as success.
	 */
	unsubscribeFromEmails(email: string) {
		return publicRequest<{ unsubscribed: boolean }>("/pub/unsubscribe", {
			method: "POST",
			body: { email },
		});
	},
};

const api = { account };

export default api;
